import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Lock, LockOpen, DiamondsFour, Lightning, UsersThree, Target, CheckCircle, ArrowRight, Sparkle, Robot, SoccerBall, Basketball, Baseball, TennisBall, Football, Trophy, Broadcast, Television, CalendarBlank, Clock, ChartBar, Flame, Snowflake, TrendUp, Newspaper } from "@phosphor-icons/react";
import { Mail } from "lucide-react";
import Marquee from "react-fast-marquee";
import MatchCard, { CompactMatchCard, LiveMatchCard } from "../components/betiq/MatchCard";
import TeamLogo from "../components/betiq/TeamLogo";
import GcrLogo from "../components/GcrLogo";
import { LanguageToggle } from "../components/LanguageToggle";
import LeagueBadge from "../components/LeagueBadge";
import ChatFAB from "../components/ChatFAB";
import BookmakersCarousel from "../components/BookmakersCarousel";
import LeagueLogo from "../components/LeagueLogo";
import RecordSection from "../components/RecordSection";
import PerformanceChart from "../components/PerformanceChart";
import ProfitSimulator from "../components/ProfitSimulator";
import { BRAND } from "../lib/brand";

// ─── API Cache (45s TTL) ───────────────────────────────────────────────────────
const _apiCache = new Map();
async function cachedFetch(url, ttl = 45_000) {
  const now = Date.now();
  const hit = _apiCache.get(url);
  if (hit && now - hit.ts < ttl) return hit.data;
  const data = await fetch(url).then(r => r.json());
  _apiCache.set(url, { data, ts: now });
  return data;
}

// ─── Hero Carousel — usa imágenes de noticias deportivas + fallback estáticas ──
const FALLBACK_SLIDES = [
  "/img/hero/hero1.jpg",  // Estadio fútbol noche, alta resolución local
  "/img/hero/hero2.jpg",  // Estadio Bernabéu noche, alta resolución local
  "/img/hero/hero3.jpg",  // Acción deportiva, alta resolución local
  "/img/hero/hero4.jpg",  // Cancha basketball, alta resolución local
];

function HeroCarousel({ slides = FALLBACK_SLIDES }) {
  const [active, setActive] = useState(0);
  // En móvil (<640px) evitamos el zoom excesivo de cover usando 100% auto + top
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  // Resiliencia: precargar y descartar imágenes rotas para no mostrar cuadros vacíos
  const [validSlides, setValidSlides] = useState(slides);
  const timer = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Precarga cada slide; solo conserva las que cargan bien
    Promise.all(slides.map(src => new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(src);
      img.onerror = () => resolve(null);
      img.src = src;
    }))).then(results => {
      if (cancelled) return;
      const ok = results.filter(Boolean);
      setValidSlides(ok.length > 0 ? ok : []);
      setActive(0);
    });
    return () => { cancelled = true; };
  }, [slides]);

  useEffect(() => {
    if (validSlides.length === 0) return;
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => setActive(p => (p + 1) % validSlides.length), 4500);
    return () => clearInterval(timer.current);
  }, [validSlides]);

  // Fallback total: gradiente oscuro con logo, sin imágenes rotas
  if (validSlides.length === 0) {
    return (
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 0,
        background: "radial-gradient(ellipse at 50% 20%, #1E1E33 0%, #111224 55%, #0A0A1E 100%)",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ opacity: 0.16, transform: "scale(2.2)" }}>
          <GcrLogo size={120} />
        </div>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.18) 45%, rgba(10,10,30,0.90) 100%)",
        }} />
      </div>
    );
  }

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 0 }}>
      {validSlides.map((src, i) => (
        <div key={src} style={{
          position: "absolute", inset: 0,
          backgroundImage: `url(${src})`,
          // Móvil: imagen al 100% del ancho, sin zoom; Desktop: cover completo
          backgroundSize: isMobile ? "100% auto" : "cover",
          backgroundPosition: isMobile ? "top center" : "center center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#0A0A1E",
          opacity: i === active ? 1 : 0,
          transition: "opacity 1.6s ease-in-out",
          willChange: "opacity",
          filter: "brightness(1.08) contrast(1.05) saturate(1.08)",
        }} />
      ))}
      {/* Overlay cinematografico: oscuro arriba para el texto, resplandor dorado abajo */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.15) 55%, rgba(10,10,30,0.92) 100%)",
      }} />
      {/* Resplandor dorado inferior */}
      <div style={{
        position: "absolute", left: 0, right: 0, bottom: 0, height: "40%",
        background: "radial-gradient(80% 100% at 50% 100%, rgba(212,175,55,0.14) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />
    </div>
  );
}

// ─── Match Card Skeletons ─────────────────────────────────────────────────────
function MatchCardSkeletons({ count = 6, compact = true }) {
  return compact ? (
    <div className="grid grid-cols-2 gap-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl h-[88px] animate-pulse" style={{ background: "#252540" }} />
      ))}
    </div>
  ) : (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl h-28 animate-pulse" style={{ background: "#252540" }} />
      ))}
    </div>
  );
}

// ─── Config ───────────────────────────────────────────────────────────────────
// Labels translated dynamically via t() in component — keys stored here
const SPORT_TABS = [
  { key: "all",      labelKey: "landing.sport_all",    Icon: Trophy },
  { key: "football", labelKey: "landing.sport_soccer",  Icon: SoccerBall },
  { key: "nba",      labelKey: "landing.sport_nba",    Icon: Basketball },
  { key: "mlb",      labelKey: "landing.sport_mlb",    Icon: Baseball },
];

// Icono de deporte en SVG (reemplaza emojis)
function SportGlyph({ sport, className = "w-3.5 h-3.5", style }) {
  const s = (sport || "").toLowerCase();
  let I = SPORT_ICONS[s] || Trophy;
  if (s.includes("baseball")) I = Baseball;
  else if (s.includes("basket")) I = Basketball;
  else if (s.includes("tennis")) I = TennisBall;
  else if (s.includes("american") || s.includes("nfl")) I = Football;
  else if (s.includes("football") || s.includes("soccer")) I = SoccerBall;
  return <I className={className} style={style} weight="fill" />;
}

// Divisor dorado suave entre secciones
function GoldDivider() {
  return (
    <div aria-hidden style={{ display: "flex", justifyContent: "center", padding: "6px 0" }}>
      <div style={{ height: 1, width: "min(680px, 82%)", background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.35), transparent)" }} />
    </div>
  );
}

const SPORT_ICONS = { football: SoccerBall, nba: Basketball, mlb: Baseball, tennis: TennisBall };

// ─── Franja de ligas oficiales ──────────────────────────────────────────
const LEAGUE_STRIP = [
  { key: "laliga",  label: "LaLiga" },
  { key: "ucl",     label: "Champions" },
  { key: "premier", label: "Premier League" },
  { key: "nba",     label: "NBA" },
  { key: "mlb",     label: "MLB" },
  { key: "nfl",     label: "NFL" },
  { key: "nhl",     label: "NHL" },
  { key: "atp",     label: "ATP" },
];
function LeagueStrip() {
  return (
    <section className="py-6 px-4" data-testid="league-strip"
      style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.04) 0%, transparent 100%)" }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-4">
          <span className="text-[10px] font-black tracking-[0.25em] uppercase" style={{ color: "#D4AF37" }}>
            Ligas que cubrimos
          </span>
        </div>
        <div className="flex items-center justify-center gap-5 sm:gap-8 flex-wrap">
          {LEAGUE_STRIP.map((lg) => (
            <div key={lg.key} className="flex flex-col items-center gap-1.5 group">
              <div className="transition-transform duration-200 group-hover:scale-110">
                <LeagueLogo sport={lg.key} size={44} />
              </div>
              <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#8080A0" }}>
                {lg.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const buildPlans = (weekly = 50, monthly = 160) => [
  {
    id: "trial",
    price: "$0",
    color: "#8080A0",
    fKeys: ["plan_trial_f1", "plan_trial_f2", "plan_trial_f3"],
    href: "/register",
    highlight: false,
  },
  {
    id: "weekly",
    price: `$${weekly}`,
    color: "#D4AF37",
    fKeys: ["plan_weekly_f1", "plan_weekly_f2", "plan_weekly_f3", "plan_weekly_f4"],
    href: "/register",
    highlight: false,
  },
  {
    id: "monthly",
    price: `$${monthly}`,
    color: "#D4AF37",
    badgeKey: "landing.plan_popular",
    fKeys: ["plan_monthly_f1", "plan_monthly_f2", "plan_monthly_f3", "plan_monthly_f4"],
    href: "/register",
    highlight: true,
  },
];
const PLANS = buildPlans();

const HOW_STEPS = [
  { n: "01", titleKey: "landing.step1_title", descKey: "landing.step1_desc", Icon: LockOpen },
  { n: "02", titleKey: "landing.step2_title", descKey: "landing.step2_desc", Icon: Broadcast },
  { n: "03", titleKey: "landing.step3_title", descKey: "landing.step3_desc", Icon: Lightning },
  { n: "04", titleKey: "landing.step4_title", descKey: "landing.step4_desc", Icon: Television },
];

// ─── Animated Counter (unificado: enteros o decimales + easing) ───────────────
function useCountUp(target, { decimals = 0, duration = 1600 } = {}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target || target === 0) return;
    setValue(0);
    const start = performance.now();
    let rafId;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(decimals > 0 ? parseFloat((eased * target).toFixed(decimals)) : Math.round(eased * target));
      if (p < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration, decimals]);
  return value;
}

// ─── Live Ticker ──────────────────────────────────────────────────────────────
function LiveTicker({ matches }) {
  const { t } = useTranslation();
  const items = matches.slice(0, 20);
  if (items.length === 0) return null;

  const TickerItem = ({ m, i }) => {
    const isLive = m.status === "in" || m.is_live;
    const isFinal = m.status === "post";
    const date = m.match_date || m.commence_time;
    let time = "";
    if (isLive && m.display_clock) {
      time = m.sport === "nba" ? `Q${m.period} ${m.display_clock}` : `${m.display_clock}'`;
    } else if (date) {
      const h = (new Date(date) - new Date()) / 3600000;
      if (h > 0 && h < 24) time = `${Math.round(h)}h`;
    }

    const homeWon = isFinal && Number(m.home_score) > Number(m.away_score);
    const awayWon = isFinal && Number(m.away_score) > Number(m.home_score);
    const pillBg = isLive
      ? "rgba(16,185,129,0.12)"
      : isFinal
      ? homeWon ? "rgba(16,185,129,0.1)" : awayWon ? "rgba(239,68,68,0.1)" : "rgba(255,255,255,0.04)"
      : "rgba(212,175,55,0.07)";

    return (
      <span
        className="inline-flex items-center gap-1.5 mx-1.5 px-3 rounded-full"
        style={{ height: 26, background: pillBg, border: `1px solid ${isLive ? "rgba(16,185,129,0.3)" : isFinal ? "rgba(255,255,255,0.07)" : "rgba(212,175,55,0.15)"}` }}
      >
        <SportGlyph sport={m.sport} className="w-3.5 h-3.5" style={{ color: "#D4AF37" }} />
        <span className="text-[11px] font-bold" style={{ color: homeWon ? "#10B981" : awayWon ? "#666" : "#D0D0D0" }}>
          {m.home_team?.split(" ").slice(-1)[0]}
        </span>
        {(isLive || isFinal) ? (
          <span className="text-[11px] font-black tracking-tighter" style={{ color: isLive ? "#fff" : "#999", fontVariantNumeric: "tabular-nums", fontFamily: "'JetBrains Mono', monospace" }}>
            {m.home_score ?? "0"}<span style={{ color: "#3A3A44", margin: "0 1px" }}>–</span>{m.away_score ?? "0"}
          </span>
        ) : (
          <span className="text-[10px] font-bold" style={{ color: "#333" }}>vs</span>
        )}
        <span className="text-[11px] font-bold" style={{ color: awayWon ? "#10B981" : homeWon ? "#666" : "#D0D0D0" }}>
          {m.away_team?.split(" ").slice(-1)[0]}
        </span>
        {isLive ? (
          <span className="text-[8px] font-black px-1 py-0.5 rounded-full" style={{ background: "#10B981", color: "#000" }}>
            {time || t("landing.ticker_live")}
          </span>
        ) : isFinal ? (
          <span className="text-[8px] font-black px-1 py-0.5 rounded" style={{ background: homeWon ? "#10B981" : awayWon ? "#ef4444" : "#333", color: homeWon || awayWon ? "#000" : "#888" }}>
            {t("landing.ticker_final")}
          </span>
        ) : time ? (
          <span className="text-[8px] font-semibold px-1 py-0.5 rounded" style={{ background: "rgba(212,175,55,0.2)", color: "#D4AF37" }}>
            {time}
          </span>
        ) : null}
      </span>
    );
  };

  return (
    <div
      style={{
        background: "linear-gradient(90deg, #050508 0%, #0A0A0E 50%, #050508 100%)",
        borderTop: "1px solid rgba(16,185,129,0.15)",
        borderBottom: "1px solid rgba(212,175,55,0.12)",
        height: 38,
        overflow: "hidden",
      }}
      data-testid="live-ticker"
    >
      {/* Fade edges */}
      <div style={{ position:"absolute", left:0, top:0, bottom:0, width:48, background:"linear-gradient(90deg,#050508,transparent)", zIndex:2, pointerEvents:"none" }} />
      <div style={{ position:"absolute", right:0, top:0, bottom:0, width:48, background:"linear-gradient(270deg,#050508,transparent)", zIndex:2, pointerEvents:"none" }} />
      <Marquee speed={35} gradient={false} pauseOnHover style={{ height: 38 }}>
        {items.map((m, i) => <TickerItem key={`${m.id || i}`} m={m} i={i} />)}
      </Marquee>
    </div>
  );
}

// ─── Sports News Feed — VIP Card Carousel ─────────────────────────────────────
// Pool of varied images per sport category — used when RSS has no real image
const SPORT_IMAGE_POOL = {
  football: [
    "https://images.unsplash.com/photo-1769859177812-d283ad3a4713?w=400&q=80",
    "https://images.unsplash.com/photo-1766525133589-e3b4b090c04b?w=400&q=80",
    "https://images.unsplash.com/photo-1773850561705-dacec6d0b16b?w=400&q=80",
    "https://images.unsplash.com/photo-1777715329839-41b5033c3003?w=400&q=80",
    "https://images.unsplash.com/photo-1748112200709-a3970815e7a1?w=400&q=80",
    "https://images.unsplash.com/photo-1667842501559-6c03ae022505?w=400&q=80",
    "https://images.unsplash.com/photo-1715276917537-c9bd2b21ce3b?w=400&q=80",
  ],
  champions: [
    "https://images.unsplash.com/photo-1659145180363-16c4f9174b05?w=400&q=80",
    "https://images.unsplash.com/photo-1676746424114-56d38af59256?w=400&q=80",
    "https://images.unsplash.com/photo-1742835232776-ed60e701f24f?w=400&q=80",
    "https://images.unsplash.com/photo-1773850561705-dacec6d0b16b?w=400&q=80",
    "https://images.unsplash.com/photo-1766525133589-e3b4b090c04b?w=400&q=80",
    "https://images.unsplash.com/photo-1769859177812-d283ad3a4713?w=400&q=80",
  ],
  baseball: [
    "https://images.unsplash.com/photo-1771208934877-abc1add680e2?w=400&q=80",
    "https://images.unsplash.com/photo-1701275533868-1555828057b0?w=400&q=80",
    "https://images.unsplash.com/photo-1728612347864-7a787847b8b6?w=400&q=80",
    "https://images.unsplash.com/8/playball.jpg?w=400&q=80",
  ],
  nba: [
    "https://images.unsplash.com/photo-1563506644863-444710df1e03?w=400&q=80",
    "https://images.unsplash.com/photo-1602619075255-d090a7443bfb?w=400&q=80",
    "https://images.unsplash.com/photo-1752166672322-f9ea3086684e?w=400&q=80",
    "https://images.unsplash.com/photo-1576683668978-61445637c506?w=400&q=80",
  ],
};

// Deterministic hash — same title always picks the same image
function strHash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function pickImage(article) {
  // 1. Use real RSS image if backend extracted one
  if (article.image_url) return article.image_url;
  // 2. Deterministic pool selection based on title
  const pool = SPORT_IMAGE_POOL[article.category] || SPORT_IMAGE_POOL.football;
  return pool[strHash(article.title || "") % pool.length];
}

function SportsNewsFeed() {
  const [articles, setArticles] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API}/api/public/sports-news`)
      .then(r => r.json())
      .then(d => { setArticles(d.articles || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [API]);

  const tabs = [
    { key: "all",       label: "Todo",      Icon: Newspaper },
    { key: "champions", label: "Champions", Icon: Trophy },
    { key: "football",  label: "Fútbol",    Icon: SoccerBall },
    { key: "baseball",  label: "MLB",       Icon: Baseball },
    { key: "nba",       label: "NBA",       Icon: Basketball },
  ];

  const filtered = activeTab === "all" ? articles : articles.filter(a => a.category === activeTab);

  if (!loading && articles.length === 0) return null;

  const skeletons = [0, 1, 2, 3];

  return (
    <div data-testid="sports-news-feed" style={{ padding: "0 0 24px" }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", padding: "0 16px 10px" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#52525B", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 3 }}>
            Actualizado ahora
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#FAFAFA", letterSpacing: "-0.02em" }}>
            Mundo Deportivo
          </div>
        </div>
        <div style={{
          fontSize: 9, fontWeight: 800, color: "#10B981",
          background: "rgba(16,185,129,0.1)",
          border: "1px solid rgba(16,185,129,0.3)",
          borderRadius: 20, padding: "4px 10px",
          letterSpacing: "0.2em", textTransform: "uppercase",
          display: "flex", alignItems: "center", gap: 5,
        }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#10B981", display: "inline-block", animation: "pulse 1.5s infinite" }} />
          EN VIVO
        </div>
      </div>

      {/* ── Category tabs ──────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 6, padding: "0 16px 12px", overflowX: "auto" }}
           className="hide-scrollbar">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            data-testid={`news-tab-${t.key}`}
            style={{
              flexShrink: 0, fontSize: 11, fontWeight: 700,
              padding: "5px 14px", borderRadius: 20, cursor: "pointer",
              border: activeTab === t.key ? "1px solid rgba(212,175,55,0.6)" : "1px solid rgba(255,255,255,0.07)",
              background: activeTab === t.key ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.03)",
              color: activeTab === t.key ? "#D4AF37" : "#71717A",
              transition: "all 0.2s", whiteSpace: "nowrap",
            }}><t.Icon className="w-3 h-3" weight="fill" style={{ display: "inline-block", verticalAlign: "-2px", marginRight: 5 }} /> {t.label}</button>
        ))}
      </div>

      {/* ── Cards carousel ─────────────────────────────────────── */}
      <div ref={scrollRef}
        style={{
          display: "flex", gap: 10,
          overflowX: "auto", paddingLeft: 16, paddingRight: 16,
          scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch",
          paddingBottom: 4,
        }}
        className="hide-scrollbar"
      >
        {(loading ? skeletons : filtered.slice(0, 8)).map((a, i) => {
          const bg = loading ? null : pickImage(a);

          return loading ? (
            <div key={i} style={{
              flexShrink: 0, width: 195, height: 130, borderRadius: 14,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              scrollSnapAlign: "start",
              animation: "pulse 1.5s ease-in-out infinite",
            }} />
          ) : (
            <a key={i} href={a.link} target="_blank" rel="noopener noreferrer"
              data-testid={`news-card-${i}`}
              style={{
                flexShrink: 0, width: 195, height: 132,
                borderRadius: 14, overflow: "hidden",
                scrollSnapAlign: "start", textDecoration: "none",
                position: "relative", display: "block",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                transition: "transform 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.02)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
            >
              {/* Background image */}
              <img src={bg} alt=""
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />

              {/* Dark gradient overlay — stronger at bottom */}
              <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0.75) 70%, rgba(0,0,0,0.95) 100%)",
              }} />

              {/* Category badge — top left */}
              <div style={{
                position: "absolute", top: 9, left: 9,
                fontSize: 9, fontWeight: 800,
                color: "#FAFAFA", letterSpacing: "0.15em", textTransform: "uppercase",
                background: "rgba(0,0,0,0.55)",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(8px)",
                borderRadius: 6, padding: "3px 8px",
                display: "flex", alignItems: "center", gap: 4,
              }}>
                <span style={{ fontSize: 10 }}>{a.emoji}</span> {a.label}
              </div>

              {/* Source — top right */}
              {a.source && (
                <div style={{
                  position: "absolute", top: 9, right: 9,
                  fontSize: 8, color: "rgba(255,255,255,0.5)",
                  background: "rgba(0,0,0,0.4)", borderRadius: 4,
                  padding: "2px 6px", letterSpacing: "0.05em",
                  maxWidth: 70, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {a.source}
                </div>
              )}

              {/* Title — bottom */}
              <div style={{
                position: "absolute", bottom: 0, left: 0, right: 0,
                padding: "10px 10px 10px",
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 800, color: "#FAFAFA",
                  lineHeight: 1.35,
                  overflow: "hidden", display: "-webkit-box",
                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                }}>
                  {a.title}
                </div>
              </div>
            </a>
          );
        })}
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}


function WeeklyHeatBar({ heat = [] }) {
  if (!heat.length) return null;

  return (
    <div data-testid="weekly-heat-bar"
      style={{
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        gap: 10, padding: "14px 0 6px",
      }}
    >
      {heat.map((d, i) => {
        const isEmpty = d.total === 0;
        const isWin   = !isEmpty && d.won >= d.lost;
        const isToday = i === heat.length - 1;

        const cWin  = { bg: "rgba(16,185,129,0.18)",  border: "rgba(16,185,129,0.6)",  text: "#10B981", glow: "0 0 18px rgba(16,185,129,0.45)" };
        const cLoss = { bg: "rgba(239,68,68,0.14)",    border: "rgba(239,68,68,0.55)",  text: "#EF4444", glow: "0 0 18px rgba(239,68,68,0.38)" };
        const cEmpty= { bg: "rgba(255,255,255,0.03)",  border: "rgba(255,255,255,0.07)", text: "#2D2D35", glow: "none" };
        const c = isEmpty ? cEmpty : isWin ? cWin : cLoss;

        const size = isToday ? 44 : 36;

        return (
          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            {/* Circle */}
            <div style={{
              width: size, height: size,
              borderRadius: "50%",
              background: c.bg,
              border: `${isToday ? "2px" : "1px"} solid ${c.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: !isEmpty ? c.glow : "none",
              position: "relative",
              transition: "all 0.3s ease",
            }}>
              {!isEmpty ? (
                <span style={{ fontSize: isToday ? 16 : 13, fontWeight: 900, color: c.text, lineHeight: 1 }}>
                  {isWin ? "✓" : "✗"}
                </span>
              ) : (
                <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#222228", display: "block" }} />
              )}
              {/* Gold "today" indicator dot */}
              {isToday && (
                <div style={{
                  position: "absolute", top: -2, right: -2,
                  width: 9, height: 9, borderRadius: "50%",
                  background: "#D4AF37",
                  border: "1.5px solid #09090B",
                  boxShadow: "0 0 8px rgba(212,175,55,0.9)",
                }} />
              )}
            </div>
            {/* Mini score */}
            {!isEmpty && (
              <div style={{ fontSize: 8, fontWeight: 800, color: c.text, textAlign: "center", lineHeight: 1, opacity: 0.8 }}>
                {d.won}{d.lost > 0 ? `-${d.lost}` : ""}
              </div>
            )}
            {/* Day label */}
            <span style={{
              fontSize: 9, fontWeight: 800,
              color: isToday ? "#C4A44A" : "#3F3F46",
              textTransform: "uppercase", letterSpacing: "0.12em",
            }}>
              {isToday ? "HOY" : d.day}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Modo Ganada — confetti + banner ─────────────────────────────────────────
function ModoGanada({ lastWinMinutes }) {
  const isHot = lastWinMinutes !== null && lastWinMinutes <= 120;
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    if (!isHot || confettiFiredRef.current) return;
    confettiFiredRef.current = true;

    // Dynamically import canvas-confetti to avoid SSR issues
    import("canvas-confetti").then(({ default: confetti }) => {
      const colors = ["#D4AF37", "#F0C96A", "#10B981", "#FAFAFA", "#D4AF37"];

      // First burst
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { x: 0.3, y: 0.6 },
        colors,
        ticks: 200,
        gravity: 0.8,
        scalar: 0.9,
      });
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { x: 0.7, y: 0.6 },
        colors,
        ticks: 200,
        gravity: 0.8,
        scalar: 0.9,
      });

      // Second burst after 600ms
      setTimeout(() => {
        confetti({
          particleCount: 40,
          spread: 100,
          origin: { x: 0.5, y: 0.5 },
          colors,
          ticks: 150,
          gravity: 1,
          scalar: 0.7,
        });
      }, 600);
    });
  }, [isHot]);

  if (!isHot) return null;

  const label = lastWinMinutes < 5   ? "¡Acabamos de ganar!"
              : lastWinMinutes < 60  ? `Ganamos hace ${lastWinMinutes} min`
              :                        `Ganamos hace ${Math.floor(lastWinMinutes / 60)}h`;

  return (
    <div data-testid="modo-ganada-banner"
      style={{
        margin: "0 16px 16px",
        borderRadius: 14,
        background: "linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(212,175,55,0.08) 100%)",
        border: "1px solid rgba(16,185,129,0.35)",
        padding: "12px 18px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        animation: "pulse-green 2s ease-in-out infinite",
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        background: "rgba(16,185,129,0.2)",
        border: "2px solid rgba(16,185,129,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16,
      }}>✓</div>
      <div>
        <div style={{
          fontSize: 12, fontWeight: 900, color: "#10B981",
          textTransform: "uppercase", letterSpacing: "0.15em",
        }}>
          {label}
        </div>
        <div style={{ fontSize: 11, color: "#71717A", marginTop: 2 }}>
          Nuestros miembros ya están cobrando · ¿Y tú?
        </div>
      </div>
      <div style={{ marginLeft: "auto", flexShrink: 0 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: "#D4AF37",
          background: "rgba(212,175,55,0.1)",
          border: "1px solid rgba(212,175,55,0.3)",
          borderRadius: 20, padding: "4px 10px",
        }}>
          ÚNETE →
        </span>
      </div>
      <style>{`
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
          50%       { box-shadow: 0 0 0 6px rgba(16,185,129,0.08); }
        }
      `}</style>
    </div>
  );
}


function WinsTicker() {
  const [wins, setWins] = useState([]);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    cachedFetch(`${API}/api/picks/public/wins`, 120_000)
      .then(d => { if (d?.wins?.length) setWins(d.wins); })
      .catch(() => {});
  }, [API]);

  // Static fallback items while loading
  const fallback = [
    { id: "f1", home_team: "Real Madrid", away_team: "Bayern Munich", odds: 2.10, sport: "football", pick_selection: "Más de 2.5 goles" },
    { id: "f2", home_team: "LA Dodgers",  away_team: "NY Yankees",    odds: 1.85, sport: "baseball", pick_selection: "LA Dodgers ML" },
    { id: "f3", home_team: "Boston Red Sox", away_team: "Detroit Tigers", odds: 2.18, sport: "baseball", pick_selection: "Detroit Tigers ML" },
    { id: "f4", home_team: "Barcelona",   away_team: "Atletico Madrid", odds: 1.70, sport: "football", pick_selection: "Barcelona -1" },
  ];

  const items = wins.length > 0 ? wins : fallback;
  // Duplicate for seamless loop
  const doubled = [...items, ...items, ...items];

  return (
    <div
      data-testid="wins-ticker"
      style={{
        background: "linear-gradient(90deg, #050508 0%, #0C0E08 50%, #050508 100%)",
        borderTop:    "1px solid rgba(16,185,129,0.2)",
        borderBottom: "1px solid rgba(16,185,129,0.12)",
        height: 42,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Left badge — fixed */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, zIndex: 10,
        display: "flex", alignItems: "center",
        background: "linear-gradient(90deg, #0A1A0E 60%, transparent 100%)",
        paddingLeft: 12, paddingRight: 28,
      }}>
        <span style={{
          fontSize: 10, fontWeight: 900, letterSpacing: "0.22em",
          color: "#10B981", textTransform: "uppercase",
          display: "flex", alignItems: "center", gap: 6,
          whiteSpace: "nowrap", textShadow: "0 1px 6px rgba(0,0,0,0.8)",
        }}>
          <span style={{ fontSize: 13 }}>✓</span> VICTORIAS
        </span>
      </div>

      {/* Fade left edge */}
      <div style={{
        position: "absolute", left: 90, top: 0, bottom: 0, width: 30, zIndex: 9,
        background: "linear-gradient(90deg, #0A1A0E, transparent)", pointerEvents: "none",
      }} />

      {/* Fade right edge */}
      <div style={{
        position: "absolute", right: 0, top: 0, bottom: 0, width: 48, zIndex: 9,
        background: "linear-gradient(270deg, #050508, transparent)", pointerEvents: "none",
      }} />

      <Marquee speed={38} gradient={false} pauseOnHover style={{ height: 42, paddingLeft: 120 }}>
        {doubled.map((w, i) => (
          <span
            key={`${w.id}-${i}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              marginRight: 44, whiteSpace: "nowrap",
            }}
          >
            {/* Green check */}
            <span style={{
              width: 18, height: 18, borderRadius: "50%",
              background: "rgba(16,185,129,0.15)",
              border: "1px solid rgba(16,185,129,0.4)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, color: "#10B981", fontWeight: 900, flexShrink: 0,
            }}>✓</span>

            {/* Sport icon */}
            <SportGlyph sport={w.sport} className="w-3.5 h-3.5" style={{ color: "#10B981" }} />

            {/* Pick selection or teams */}
            <span style={{ fontSize: 13, fontWeight: 800, color: "#F4F4F5", textShadow: "0 1px 6px rgba(0,0,0,0.7)" }}>
              {w.pick_selection || `${w.home_team} vs ${w.away_team}`}
            </span>

            {/* Odds */}
            {w.odds && (
              <span style={{
                fontSize: 12, fontWeight: 900,
                color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace",
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.2)",
                borderRadius: 4, padding: "1px 5px",
              }}>
                {Number(w.odds).toFixed(2)}
              </span>
            )}

            {/* Dot separator */}
            <span style={{ color: "rgba(255,255,255,0.1)", fontSize: 14, marginLeft: 2 }}>·</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

// Banner de conversión cuando hay 2+ IQ publicados
function TwoIQBanner({ count, onUnlock }) {
  const { t } = useTranslation();
  return (
    <div
      className="relative overflow-hidden rounded-xl mb-3"
      data-testid="two-iq-banner"
      style={{
        background: "linear-gradient(135deg, #0F0E07 0%, #1A1500 50%, #0F0E07 100%)",
        border: "1px solid rgba(212,175,55,0.45)",
      }}
    >
      {/* Glow stripe top */}
      <div style={{ height: 2, background: "linear-gradient(90deg, transparent, #D4AF37, #F0C96A, #D4AF37, transparent)" }} />

      <div className="px-4 py-4 text-center">
        {/* Badge pulsante */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3"
          style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.35)" }}>
          <span className="w-2 h-2 rounded-full inline-block animate-pulse" style={{ background: "#D4AF37" }} />
          <span className="text-[11px] font-black tracking-widest uppercase" style={{ color: "#D4AF37" }}>
            {t("landing.two_iq_badge", { count })}
          </span>
        </div>
        {/* Título */}
        <div className="font-sport text-base font-black text-white leading-snug mb-1">
          {t("landing.two_iq_title", { count })}
        </div>
        {/* Subtítulo */}
        <div className="text-xs leading-relaxed" style={{ color: "#A1A1AA" }}>
          {t("landing.two_iq_sub")}
        </div>
      </div>
    </div>
  );
}

// Tarjeta IQ completamente difuminada — solo visible para miembros
function CompactSignalCard({ isAuthenticated, index }) {
  const { t } = useTranslation();

  return (
    <div
      className="relative overflow-hidden"
      data-testid={`compact-signal-card-${index}`}
      style={{ background: "#252540", border: "2px solid rgba(212,175,55,0.4)", borderRadius: 12, minHeight: 160 }}
    >
      {/* Top bar */}
      <div style={{ height: 2, background: "linear-gradient(90deg, #D4AF37, #10B981, #D4AF37)" }} />

      {/* Contenido siempre difuminado (placeholder visual) */}
      <div
        className="px-3 pt-3 pb-3 pointer-events-none select-none"
        style={{ filter: "blur(9px)", userSelect: "none" }}
        aria-hidden="true"
      >
        {/* Badge fake */}
        <div className="inline-flex items-center gap-1 px-2 py-1 mb-3 rounded"
          style={{ background: "rgba(212,175,55,0.15)", width: "70%" }}>
          <div className="w-2 h-2 rounded-full" style={{ background: "#D4AF37" }} />
          <div className="h-2 rounded flex-1" style={{ background: "#D4AF37", opacity: 0.6 }} />
        </div>
        {/* Equipo local */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full flex-shrink-0" style={{ background: "rgba(255,255,255,0.1)" }} />
          <div className="h-3 rounded flex-1" style={{ background: "#3F3F46" }} />
        </div>
        <div className="h-2 rounded mb-2 ml-9" style={{ background: "rgba(255,255,255,0.1)", width: "20%" }} />
        {/* Equipo visitante */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-full flex-shrink-0" style={{ background: "rgba(255,255,255,0.1)" }} />
          <div className="h-3 rounded flex-1" style={{ background: "#3F3F46" }} />
        </div>
        {/* IQ Score fake */}
        <div className="flex items-center justify-between px-3 py-2 rounded"
          style={{ background: "rgba(16,185,129,0.08)" }}>
          <div className="h-2 rounded w-6" style={{ background: "rgba(255,255,255,0.1)" }} />
          <div className="h-5 rounded w-10" style={{ background: "#10B981", opacity: 0.5 }} />
        </div>
      </div>

      {/* Lock overlay encima del blur */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-2"
        data-testid={`signal-lock-compact-${index}`}
        style={{ zIndex: 10 }}
      >
        <div className="flex items-center justify-center w-10 h-10 rounded-full"
          style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(212,175,55,0.3)" }}>
          <Lock weight="fill" className="w-5 h-5" style={{ color: "#D4AF37" }} />
        </div>
        <div className="text-[10px] font-black tracking-widest text-center px-2"
          style={{ color: "#D4AF37" }}>
          {t("landing.two_iq_locked")}
        </div>
        <div className="text-[9px] font-bold text-center" style={{ color: "#52525B" }}>
          IQ #{index + 1}
        </div>
      </div>
    </div>
  );
}

// ─── Ejemplo de señal ganadora — para que el visitante VEA cómo se ve una IQ ──
function WonExampleCard() {
  const [win, setWin] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    cachedFetch(`${API}/api/picks/public/wins`, 300_000)
      .then(d => { if (d?.wins?.length) setWin(d.wins[0]); })
      .catch(() => {});
  }, [API]);

  // Ejemplo estático si el endpoint no responde
  const ex = win || {
    home_team: "Real Madrid", away_team: "Girona",
    pick_selection: "Real Madrid gana", odds: 1.72, sport: "football",
  };

  return (
    <div data-testid="won-example-card" style={{
      background: "linear-gradient(160deg, rgba(16,185,129,0.10) 0%, rgba(5,5,7,0.75) 65%)",
      border: "1px solid rgba(16,185,129,0.30)",
      borderRadius: 16, overflow: "hidden", marginBottom: 4,
    }}>
      <div style={{ height: 3, background: "linear-gradient(90deg, #10B981, var(--gold), #10B981)" }} />
      <div style={{ padding: "18px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{
            fontSize: 10, fontWeight: 900, letterSpacing: "0.14em", textTransform: "uppercase",
            color: "var(--gold)",
          }}>Así se ve una señal IQ</span>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 11, fontWeight: 800, color: "#fff",
            background: "rgba(16,185,129,0.18)", border: "1px solid rgba(16,185,129,0.45)",
            padding: "4px 10px", borderRadius: 999,
          }}>
            <CheckCircle weight="fill" className="w-3.5 h-3.5" style={{ color: "#10B981" }} />
            GANADA
          </span>
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginBottom: 4 }}>
          {ex.home_team} vs {ex.away_team}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: 13, color: "#C9C9DD" }}>{ex.pick_selection}</span>
          <span style={{ fontSize: 20, fontWeight: 800, color: "var(--gold)" }}>
            {typeof ex.odds === "number" ? ex.odds.toFixed(2) : ex.odds}
          </span>
        </div>
        <div style={{ fontSize: 12, color: "#9090AA", lineHeight: 1.6 }}>
          Esto es lo que reciben los miembros cada día: el partido, la jugada exacta y la cuota —
          sin adivinanzas.
        </div>
      </div>
    </div>
  );
}

// Tarjeta IQ normal (cuando solo hay 1 señal) — ligeramente compactada
function SignalCard({ signal, isAuthenticated, onUnlock }) {
  const { t } = useTranslation();
  if (!signal) {
    return (
      <div className="relative overflow-hidden rounded-2xl p-6 text-center mb-4" data-testid="signal-card-empty"
        style={{
          background: "linear-gradient(160deg, rgba(212,175,55,0.09) 0%, rgba(5,5,7,0.75) 65%)",
          border: "1px solid rgba(212,175,55,0.28)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}>
        {/* Vista previa difuminada */}
        <div aria-hidden style={{ filter: "blur(7px)", opacity: 0.55, pointerEvents: "none", userSelect: "none" }}>
          <div style={{ height: 12, borderRadius: 6, background: "rgba(255,255,255,0.14)", margin: "0 auto 8px", maxWidth: 220 }} />
          <div style={{ height: 12, borderRadius: 6, background: "rgba(255,255,255,0.09)", margin: "0 auto 18px", maxWidth: 150 }} />
        </div>
        <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
          style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.55)", boxShadow: "0 0 24px rgba(212,175,55,0.25)" }}>
          <Lock weight="fill" className="w-5 h-5" style={{ color: "#D4AF37" }} />
        </div>
        <div className="font-sport text-base text-white mb-1">{t("landing.iq_soon")}</div>
        <div className="text-xs mb-4" style={{ color: "#9090AA" }}>{t("landing.iq_soon_sub")}</div>
        <button onClick={onUnlock} data-testid="iq-notify-btn"
          className="btn-gold btn-lift px-6 py-2.5 text-xs font-black tracking-widest uppercase rounded-xl">
          {t("landing.iq_notify_cta")}
        </button>
      </div>
    );
  }

  const matchDate = signal.match_date
    ? new Date(signal.match_date).toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" })
    : null;
  const matchTime = signal.match_date
    ? new Date(signal.match_date).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) + " (hora local)"
    : null;

  const formHome = signal.stats?.form_home || "";
  const formAway = signal.stats?.form_away || "";

  return (
    <div className="overflow-hidden mb-3 card-signal-glow" data-testid="signal-card"
      style={{ background: "#252540", border: "2px solid #D4AF37", borderRadius: 12 }}>

      {/* Top bar premium */}
      <div style={{ height: 3, background: "linear-gradient(90deg, #D4AF37, #10B981, #D4AF37)" }} />

      <div className="px-5 pt-4 pb-3">
        {/* Badge IQ del Día */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 mb-3 badge-glass"
          style={{ border: "1px solid rgba(212,175,55,0.2)", borderRadius: 6 }}>
          <DiamondsFour weight="fill" className="w-3 h-3" style={{ color: "#D4AF37" }} />
          <span className="font-sport text-[11px] tracking-widest" style={{ color: "#D4AF37" }}>{t("landing.iq_title")}</span>
        </div>

        {/* Partido */}
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <TeamLogo logoUrl={signal.home_logo} teamName={signal.home_team} size={38} rounded="md" />
              <span className="font-sport text-lg text-white leading-tight truncate">{signal.home_team}</span>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0" style={{ background: "rgba(16,185,129,0.1)", color: "#10B981", border: "1px solid rgba(16,185,129,0.2)" }}>{t("landing.home_label")}</span>
          </div>
          <div className="text-[11px] font-bold mb-1.5 pl-[50px]" style={{ color: "#52525B", letterSpacing: "0.1em" }}>VS</div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5 min-w-0">
              <TeamLogo logoUrl={signal.away_logo} teamName={signal.away_team} size={38} rounded="md" />
              <span className="font-sport text-lg text-white leading-tight truncate">{signal.away_team}</span>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0" style={{ background: "rgba(212,175,55,0.06)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.18)" }}>{t("landing.away_label")}</span>
          </div>
        </div>

        {/* Fecha y liga */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <LeagueBadge sport={signal.sport} size="sm" />
          <span className="text-xs font-semibold" style={{ color: "#71717A" }}>{signal.league}</span>
          {matchDate && <span className="text-xs font-bold" style={{ color: "#A1A1AA" }}><CalendarBlank className="w-3.5 h-3.5" weight="fill" style={{ display: "inline-block", verticalAlign: "-2px", marginRight: 4 }} />{matchDate}</span>}
          {matchTime && <span className="text-xs" style={{ color: "#52525B" }}><Clock className="w-3.5 h-3.5" weight="fill" style={{ display: "inline-block", verticalAlign: "-2px", marginRight: 4 }} />{matchTime}</span>}
        </div>

        {/* Últimos resultados */}
        {(formHome || formAway) && (
          <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}>
            {formHome && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#52525B" }}>{signal.home_team?.split(" ")[0]}</span>
                {formHome.slice(0, 2).split("").map((r, i) => {
                  const cfg = { W: { bg: "#10B981", lbl: t("landing.win_badge") }, L: { bg: "#ef4444", lbl: t("landing.loss_badge") }, D: { bg: "#D4AF37", lbl: t("landing.draw_badge") } };
                  const c = cfg[r] || { bg: "#26262E", lbl: r };
                  return <span key={i} style={{ background: c.bg, color: "#000", width: 18, height: 18, borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 900 }}>{c.lbl}</span>;
                })}
              </div>
            )}
            {formHome && formAway && <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.1)" }} />}
            {formAway && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#52525B" }}>{signal.away_team?.split(" ")[0]}</span>
                {formAway.slice(0, 2).split("").map((r, i) => {
                  const cfg = { W: { bg: "#10B981", lbl: t("landing.win_badge") }, L: { bg: "#ef4444", lbl: t("landing.loss_badge") }, D: { bg: "#D4AF37", lbl: t("landing.draw_badge") } };
                  const c = cfg[r] || { bg: "#26262E", lbl: r };
                  return <span key={i} style={{ background: c.bg, color: "#000", width: 18, height: 18, borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 900 }}>{c.lbl}</span>;
                })}
              </div>
            )}
          </div>
        )}

        {/* IQ Score */}
        <div className="flex items-center justify-between px-4 py-2.5 rounded mb-1"
          style={{ background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.15)" }} data-testid="iq-score-box">
          <span className="text-xs font-bold tracking-widest uppercase" style={{ color: "#52525B" }}>{t("landing.iq_score_label")}</span>
          <span className="font-sport text-3xl font-black" style={{ color: "#10B981" }}>
            {signal.iq_score}.0
          </span>
        </div>
      </div>

      {/* Contenido blur */}
      <div className="relative px-5 pb-3">
        <div className="space-y-2 pointer-events-none"
          style={{ filter: isAuthenticated ? "none" : "blur(8px)", transition: "filter 0.3s" }}
          data-testid="signal-blurred-content" aria-hidden={!isAuthenticated}>
          {[{ color: "#10B981", w: "65%" }, { color: "#D4AF37", w: "80%" }, { color: "#A1A1AA", w: "50%" }].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: item.color, opacity: 0.4 }} />
              <div className="h-2.5 rounded" style={{ background: "rgba(255,255,255,0.1)", width: item.w }} />
            </div>
          ))}
        </div>
        {!isAuthenticated && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-center"
            data-testid="signal-lock-overlay">
            <Lock weight="fill" className="w-5 h-5" style={{ color: "#52525B" }} />
            <div className="font-sport text-sm font-bold" style={{ color: "#D4AF37" }}>{t("landing.premium_content")}</div>
            <div className="text-xs" style={{ color: "#52525B" }}>{t("landing.login_to_view")}</div>
          </div>
        )}
      </div>

      {!isAuthenticated && (
        <div className="px-5 pb-4">
          <button onClick={onUnlock} data-testid="unlock-signal-btn"
            className="btn-primary w-full py-3 text-sm text-center">
            {t("landing.one_iq_cta")}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Hot / Cold Teams Section ─────────────────────────────────────────────────
const STREAK_SPORT_TABS = [
  { key: "football", labelKey: "landing.sport_soccer", logo: "laliga" },
  { key: "nba",      labelKey: "landing.sport_nba",    logo: "nba" },
  { key: "mlb",      labelKey: "landing.sport_mlb",    logo: "mlb" },
];

function FormDot({ result }) {
  const { t } = useTranslation();
  const cfg = {
    W: { bg: "#10B981", label: t("landing.win_badge"), title: t("landing.win_title") },
    L: { bg: "#ef4444", label: t("landing.loss_badge"), title: t("landing.loss_title") },
    D: { bg: "#D4AF37", label: t("landing.draw_badge"), title: t("landing.draw_title") },
  };
  const { bg, label, title } = cfg[result] || { bg: "#2a2a2a", label: result, title: result };
  return (
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-black flex-shrink-0"
      style={{ background: bg, color: "#000" }}
      title={title}
    >
      {label}
    </span>
  );
}

function TeamRow({ team, hot }) {
  const streakColor = hot ? "#10B981" : "#ef4444";
  const streakCount = hot ? team.wins : team.losses;
  const streakLabel = hot ? `${streakCount} VICTORIAS` : `${streakCount} DERROTAS`;
  return (
    <div
      className="rounded-2xl p-4 mb-3 relative overflow-hidden"
      style={{
        background: hot
          ? "linear-gradient(135deg, rgba(16,185,129,0.12) 0%, #1e1e38 50%, #1a1a32 100%)"
          : "linear-gradient(135deg, rgba(239,68,68,0.10) 0%, #1e1e38 50%, #1a1a32 100%)",
        border: `1px solid ${hot ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
        boxShadow: hot
          ? "0 8px 24px rgba(16,185,129,0.12), inset 0 1px 0 rgba(16,185,129,0.2)"
          : "0 8px 24px rgba(239,68,68,0.10), inset 0 1px 0 rgba(239,68,68,0.15)",
      }}
      data-testid="team-row"
    >
      {/* Resplandor lateral */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
        background: `linear-gradient(180deg, ${streakColor}, ${streakColor}66)`,
        boxShadow: `0 0 16px ${streakColor}`,
      }} />
      <div className="flex items-center gap-3 pl-2">
        <div className="flex-1 min-w-0">
          <div className="text-base font-black text-white truncate" style={{ letterSpacing: "-0.01em" }}>{team.team}</div>
          <div className="text-[11px] mt-1 truncate" style={{ color: "#9090B0" }}>{team.stat_label}</div>
          {/* Racha destacada */}
          <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full"
            style={{
              background: hot ? "rgba(16,185,129,0.16)" : "rgba(239,68,68,0.16)",
              border: `1px solid ${hot ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)"}`,
              boxShadow: `0 0 14px ${hot ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.25)"}`,
            }}>
            <span style={{ fontSize: 13 }}>{hot ? "🔥" : "❄️"}</span>
            <span className="text-[11px] font-black tracking-wide" style={{ color: streakColor }}>
              {streakLabel}
            </span>
          </div>
        </div>
        {/* Últimos resultados */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {(team.form || []).slice(0, 5).map((r, i) => (
            <div key={`${r}-${i}`}
              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black"
              style={{
                background: r === "won" || r === "W" || r === "V" ? "#10B981" : "#ef4444",
                color: "#fff",
                boxShadow: `0 0 10px ${r === "won" || r === "W" || r === "V" ? "rgba(16,185,129,0.5)" : "rgba(239,68,68,0.5)"}`,
              }}>
              {r === "won" || r === "W" || r === "V" ? "V" : "D"}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HotColdSection({ data }) {
  const { t } = useTranslation();
  const [activeSport, setActiveSport] = useState("mlb");
  const bySport = data?.by_sport || {};
  const current = bySport[activeSport] || { hot: [], cold: [] };
  const hasData = current.hot.length > 0 || current.cold.length > 0;

  return (
    <section className="py-10 px-4" style={{ background: "#1A1A2E", borderTop: "1px solid rgba(255,255,255,0.1)" }} data-testid="hot-cold-section">
      <div className="max-w-xl mx-auto">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-[11px] font-black tracking-widest uppercase mb-1" style={{ color: "#D4AF37" }}>{t("landing.streak_subtitle")}</div>
            <h2 className="text-xl font-black text-white">{t("landing.streak_heading")}</h2>
          </div>
        </div>

        {/* Sport tabs */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          {STREAK_SPORT_TABS.map((tab) => {
            const active = activeSport === tab.key;
            const sportData = bySport[tab.key] || { hot: [], cold: [] };
            const count = sportData.hot.length + sportData.cold.length;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveSport(tab.key)}
                data-testid={`streak-tab-${tab.key}`}
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl text-xs font-semibold transition-all duration-150 relative"
                style={active
                  ? { background: "linear-gradient(160deg, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.04) 100%)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.35)", boxShadow: "0 0 20px rgba(212,175,55,0.12)" }
                  : { background: "linear-gradient(160deg, #282842 0%, #20203a 100%)", color: "#9090AA", border: "1px solid rgba(255,255,255,0.1)" }
                }
              >
                <LeagueLogo sport={tab.logo} size={30} />
                <span className="text-[11px] font-bold">{t(tab.labelKey)}</span>
                {count > 0 && (
                  <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full"
                    style={{ background: active ? "rgba(212,175,55,0.15)" : "#252540", color: active ? "#D4AF37" : "#555" }}>
                    {t("landing.teams_count_label", { count })}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {!hasData ? (
          <div className="rounded-xl p-8 text-center" style={{ background: "#252540", border: "1px solid rgba(255,255,255,0.1)" }}>
            <ChartBar className="w-8 h-8 mx-auto mb-2" weight="fill" style={{ color: "#52525B" }} />
            <div className="text-sm font-semibold text-white mb-1">{t("landing.no_streak_data")}</div>
            <div className="text-xs" style={{ color: "#9898B8" }}>
              {activeSport === "football" ? t("landing.soccer_offseason") : t("landing.no_recent_games")}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {current.hot.length > 0 && (
              <div>
                <div className="flex items-center gap-2 px-1 mb-3">
                  <span style={{ fontSize: 18 }}>🔥</span>
                  <span className="text-base font-black text-white tracking-tight">{t("landing.hot_streak")}</span>
                  <span className="ml-auto text-[11px] font-black px-3 py-1.5 rounded-full"
                    style={{ background: "rgba(16,185,129,0.15)", color: "#10B981", border: "1px solid rgba(16,185,129,0.3)" }}>
                    {t("landing.teams_count_label", { count: current.hot.length })}
                  </span>
                </div>
                <div>
                  {current.hot.map((team) => <TeamRow key={team.team} team={team} hot={true} />)}
                </div>
              </div>
            )}
            {current.cold.length > 0 && (
              <div>
                <div className="flex items-center gap-2 px-1 mb-3">
                  <span style={{ fontSize: 18 }}>❄️</span>
                  <span className="text-base font-black text-white tracking-tight">{t("landing.cold_streak")}</span>
                  <span className="ml-auto text-[11px] font-black px-3 py-1.5 rounded-full"
                    style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)" }}>
                    {t("landing.teams_count_label", { count: current.cold.length })}
                  </span>
                </div>
                <div>
                  {current.cold.map((team) => <TeamRow key={team.team} team={team} hot={false} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Main Landing ─────────────────────────────────────────────────────────────
export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const isAuthenticated = !!user;

  const [selectedSport, setSelectedSport] = useState("all");
  const [selectedOdd, setSelectedOdd] = useState(null);
  const [signal, setSignal] = useState(null);
  const [signals, setSignals] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [publicStats, setPublicStats] = useState(null);
  const [hotColdTeams, setHotColdTeams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeklyHeat, setWeeklyHeat] = useState([]);
  const [lastWinMinutes, setLastWinMinutes] = useState(null);
  const [heroSlides, setHeroSlides] = useState(FALLBACK_SLIDES);
  const [plans, setPlans] = useState(PLANS);

  const base = process.env.REACT_APP_BACKEND_URL;

  // Precios dinámicos del admin
  useEffect(() => {
    cachedFetch(`${base}/api/public/pricing`, 600_000)
      .then((res) => {
        const w = res?.data?.weekly ?? res?.weekly ?? 50;
        const m = res?.data?.monthly ?? res?.monthly ?? 160;
        setPlans(buildPlans(w, m));
      })
      .catch(() => {});
  }, [base]);

  // Animated counters — driven by real API data from /api/public/stats
  // Contadores animados del hero (unificados)
  const winRateFx    = useCountUp(publicStats?.win_rate || 0, { decimals: 1 });
  const totalPicksFx = useCountUp(publicStats?.total_picks || 0);
  const totalUsersFx = useCountUp(publicStats?.total_users || publicStats?.active_users || 0);

  // ── Normalize team name for fuzzy matching ─────────────────────────────────
  const normTeam = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const teamMatch = useCallback((a, b) => {
    const na = normTeam(a), nb = normTeam(b);
    if (!na || !nb) return false;
    return na === nb || na.startsWith(nb.slice(0, 5)) || nb.startsWith(na.slice(0, 5));
  }, []);

  // ── Sync merge of live scores into match list ──────────────────────────────
  const mergeWithLive = useCallback((matches, liveEvents) => {
    if (!liveEvents || liveEvents.length === 0) return matches;
    const usedIds = new Set();
    const updated = matches.map(m => {
      let fresh = liveEvents.find(l => l.id === m.id);
      if (!fresh) fresh = liveEvents.find(l =>
        teamMatch(l.home_team, m.home_team) && teamMatch(l.away_team, m.away_team)
      );
      if (!fresh) return m;
      usedIds.add(fresh.id);
      return { ...m, home_score: fresh.home_score, away_score: fresh.away_score, display_clock: fresh.display_clock, period: fresh.period, status: "in", status_desc: fresh.status_desc, is_live: true };
    });
    liveEvents.forEach(live => {
      if (!usedIds.has(live.id)) updated.unshift({ ...live, is_live: true });
    });
    return updated;
  }, [teamMatch]);

  // ── FASE 1: Carga crítica — signal + partidos + live (muestra contenido en ~1s) ─
  useEffect(() => {
    const fetchCritical = async () => {
      try {
        const [signalRes, eventsRes, oddsRes, liveRes, winsRes] = await Promise.allSettled([
          cachedFetch(`${base}/api/public/signal-today`, 30_000),
          cachedFetch(`${base}/api/events/upcoming?limit=20`, 45_000),
          cachedFetch(`${base}/api/public/odds-featured`, 30_000),
          fetch(`${base}/api/public/events/live`).then(r => r.json()),
          cachedFetch(`${base}/api/picks/public/wins`, 120_000),
        ]);

        if (signalRes.status === "fulfilled" && signalRes.value?.signal) {
          setSignal(signalRes.value.signal);
          setSignals(signalRes.value?.signals || [signalRes.value.signal]);
        }

        if (winsRes.status === "fulfilled" && winsRes.value) {
          if (winsRes.value.weekly_heat?.length) setWeeklyHeat(winsRes.value.weekly_heat);
          if (winsRes.value.last_win_minutes !== undefined) setLastWinMinutes(winsRes.value.last_win_minutes);
        }

        const merged = [];
        if (eventsRes.status === "fulfilled") {
          Object.entries(eventsRes.value || {}).forEach(([sport, events]) => {
            (events || []).forEach(ev => merged.push({ ...ev, sport }));
          });
        }
        if (oddsRes.status === "fulfilled" && oddsRes.value?.events) {
          oddsRes.value.events.forEach(oddsEv => {
            const idx = merged.findIndex(m => m.home_team === oddsEv.home_team && m.away_team === oddsEv.away_team);
            if (idx !== -1) {
              merged[idx].home_odds = oddsEv.home_odds;
              merged[idx].away_odds = oddsEv.away_odds;
              merged[idx].draw_odds = oddsEv.draw_odds;
              if (oddsEv.is_live) merged[idx].is_live = true;
            } else {
              merged.push({
                id: `odds_${oddsEv.home_team}_${oddsEv.away_team}`,
                sport: oddsEv.sport, league: oddsEv.league,
                home_team: oddsEv.home_team, away_team: oddsEv.away_team,
                match_date: oddsEv.commence_time,
                status: oddsEv.is_live ? "in" : "pre", is_live: oddsEv.is_live,
                home_odds: oddsEv.home_odds, away_odds: oddsEv.away_odds, draw_odds: oddsEv.draw_odds,
              });
            }
          });
        }
        const liveEvents = liveRes.status === "fulfilled" ? liveRes.value.events || [] : [];
        setAllMatches(mergeWithLive(merged, liveEvents));
      } finally {
        setLoading(false); // ← página visible aquí (~1.3s)
      }
    };
    fetchCritical();
    const interval = setInterval(fetchCritical, 120_000);
    return () => clearInterval(interval);
  }, [base, mergeWithLive]);

  // ── FASE 2: Carga diferida — stats + hot-cold (NO bloquea la página) ────────
  useEffect(() => {
    const fetchDeferred = () => {
      // stats
      cachedFetch(`${base}/api/public/stats`, 15_000)
        .then(data => { if (data) setPublicStats(data); })
        .catch(() => {});
      // hot-cold — puede tardar 1-2s (ya cacheado 1h en backend)
      cachedFetch(`${base}/api/public/teams/hot-cold`, 60_000)
        .then(data => { if (data?.by_sport) setHotColdTeams(data); })
        .catch(() => {});
    };
    // Espera 200ms para no competir con la carga crítica
    const timer = setTimeout(fetchDeferred, 200);
    return () => clearTimeout(timer);
  }, [base]);

  // ── FASE 3: Imágenes dinámicas para Hero Carousel (diferida, no bloquea) ─────
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        // Hero usa siempre las imágenes locales en alta resolución.
        // (Antes se reemplazaban por imágenes de noticias RSS de calidad impredecible.)
      } catch (_) {
        // fallback silencioso — las imágenes estáticas siguen funcionando
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [base]);

  // ── Live score polling: every 30s, only ESPN (no API key limit) ────────────
  useEffect(() => {
    const hasLive = allMatches.some(m => m.status === "in" || m.is_live);
    if (!hasLive) return;

    const fetchLive = async () => {
      try {
        const res = await fetch(`${base}/api/public/events/live`).then(r => r.json());
        const liveEvents = res.events || [];
        if (liveEvents.length === 0) return;
        setAllMatches(prev => {
          const updated = prev.map(m => {
            const fresh = liveEvents.find(l => l.id === m.id);
            if (!fresh) return m;
            return {
              ...m,
              home_score:    fresh.home_score,
              away_score:    fresh.away_score,
              display_clock: fresh.display_clock,
              period:        fresh.period,
              status:        fresh.status,
              status_desc:   fresh.status_desc,
            };
          });
          return updated;
        });
      } catch (_) {}
    };

    const interval = setInterval(fetchLive, 30000);
    return () => clearInterval(interval);
  }, [allMatches.some(m => m.status === "in" || m.is_live), base]);

  // Sort helper: by match date/time ascending
  const byTime = (a, b) => {
    const ta = new Date(a.match_date || a.commence_time || 0).getTime();
    const tb = new Date(b.match_date || b.commence_time || 0).getTime();
    return ta - tb;
  };

  const filteredMatches = allMatches.filter(m => {
    if (selectedSport === "tennis") return false;
    if (selectedSport === "all") return m.sport !== "tennis";
    return m.sport === selectedSport;
  });

  const liveMatches    = [...filteredMatches.filter(m => m.status === "in" || m.is_live)].sort(byTime);
  const upcomingMatches = [...filteredMatches.filter(m => m.status === "pre")].sort(byTime);
  const recentMatches  = [...filteredMatches.filter(m => m.status === "post")].sort((a, b) => byTime(b, a));

  // In "TODOS" view: all sports mixed, pure chronological order (live first, then by time)
  const allDayMatches = selectedSport === "all"
    ? [...filteredMatches.filter(m => m.status !== "in" && !m.is_live)].sort(byTime)
    : [];

  return (
    <div style={{ background: "#1A1A2E", minHeight: "100vh", color: "#fff", fontFamily: "'Manrope', sans-serif" }}>

      {/* ── HEADER ──────────────────────────────────────────────── */}
      <header className="glass-nav sticky top-0 z-50 px-4 py-3 flex items-center justify-between gap-2"
        data-testid="landing-header">
        <Link to="/" data-testid="betiq-logo">
          <GcrLogo size={34} />
        </Link>
        <div className="flex items-center gap-2 ml-auto">
          <LanguageToggle />
          {/* Chat Asesor — visible for all */}
          <Link to="/chat" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors"
            style={{ color: "#D4AF37", background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.2)" }}
            data-testid="header-chat-btn">
            <Robot weight="fill" className="w-3.5 h-3.5" /> Asesor IA
          </Link>
          {user ? (
            <button onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")}
              className="btn-primary px-5 py-2 text-sm" data-testid="go-dashboard-btn">
              {t("nav.panel")}
            </button>
          ) : (
            <>
              <Link to="/register" className="px-4 py-2 text-xs font-semibold hidden sm:block"
                style={{ color: "#A1A1AA", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "6px" }} data-testid="header-register-btn">
                {t("nav.register")}
              </Link>
              <Link to="/login" className="btn-primary px-5 py-2 text-sm" data-testid="header-login-btn">
                {t("nav.login")}
              </Link>
            </>
          )}
        </div>
      </header>

      {/* ── LIVE TICKER ─────────────────────────────────────────── */}
      {allMatches.length > 0 && <LiveTicker matches={allMatches} />}

      {/* ── HERO ────────────────────────────────────────────────── */}
      <section
        className="relative text-center px-4 pt-20 pb-14 overflow-hidden"
        data-testid="hero-section"
        style={{ minHeight: 460 }}
      >
        {/* Animated background carousel */}
        <HeroCarousel slides={heroSlides} />

        {/* Subtle radial */}
        <div className="absolute inset-0 bg-radial-subtle pointer-events-none" aria-hidden style={{ zIndex: 1 }} />

        <div className="relative max-w-lg mx-auto" style={{ zIndex: 2 }}>
          {/* Badge EN VIVO — solo visible cuando hay partidos en vivo */}
          {(() => {
            const liveCount = allMatches.filter(m => m.status === "in" || m.is_live).length;
            if (liveCount === 0) return null;
            return (
              <div className="badge-live badge-glass inline-flex items-center gap-2 px-3 py-1.5 mb-6 fade-in-up">
                <span className="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style={{ background: "#10B981" }} />
                <span className="text-[11px]">
                  {BRAND.name} · {liveCount} {t("landing.live_badge")}
                </span>
              </div>
            );
          })()}

          {/* Main headline */}
          <h1 className="font-sport title-depth mb-4 leading-[1.02] fade-in-up-1" style={{ fontSize: "clamp(3rem, 11vw, 6rem)", letterSpacing: "-0.04em", textShadow: "0 4px 30px rgba(0,0,0,0.9), 0 2px 8px rgba(0,0,0,0.8)" }}>
            {t("landing.hero_title")}<br />
            <span className="text-gradient-gold" style={{ filter: "drop-shadow(0 4px 20px rgba(212,175,55,0.4))" }}>{t("landing.hero_title_gold")}</span>
          </h1>
          <p className="text-base mb-8 leading-relaxed max-w-sm mx-auto" style={{ color: "#D1D5DB", fontStyle: "italic", textShadow: "0 1px 8px rgba(0,0,0,0.8)" }}>
            {t("landing.hero_subtitle")}<br />
            <span style={{ color: "#FFFFFF", fontStyle: "normal", fontWeight: 600 }}>{t("landing.hero_subtitle_question")}</span>
          </p>

          {/* Stats bar — modern */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            {[
              { label: t("landing.stat_winrate"), hasData: !!(publicStats && publicStats.win_rate > 0),
                display: `${winRateFx.toFixed(1)}%`, raw: publicStats?.win_rate || 0,
                sub: t("landing.stat_winrate_sub"), color: "#10B981", color2: "#6EE7B7",
                glow: "rgba(16,185,129,0.35)", Icon: TrendUp, bar: true },
              { label: t("landing.stat_picks"), hasData: !!(publicStats && totalPicksFx > 0),
                display: `${Math.round(totalPicksFx)}`,
                sub: t("landing.stat_picks_sub"), color: "#D4AF37", color2: "#F6E27A",
                glow: "rgba(212,175,55,0.35)", Icon: Trophy },
              { label: t("landing.stat_users"), hasData: !!(publicStats && totalUsersFx > 0),
                display: `${Math.round(totalUsersFx)}+`,
                sub: t("landing.stat_users_sub"), color: "#C0A0FF", color2: "#E9D5FF",
                glow: "rgba(192,160,255,0.30)", Icon: UsersThree },
            ].map((stat, i) => (
              <div key={i} data-testid={`hero-stat-${i}`} className="fade-in-up" style={{
                animationDelay: `${0.15 + i * 0.12}s`,
                background: "linear-gradient(160deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.09), 0 14px 34px rgba(0,0,0,0.45)",
                borderRadius: "18px",
                padding: "16px 8px 14px",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}>
                {/* Radial accent glow */}
                <div style={{
                  position: "absolute", inset: 0, pointerEvents: "none",
                  background: `radial-gradient(120% 90% at 50% 0%, ${stat.glow} 0%, transparent 60%)`,
                }} />
                {/* Top accent line */}
                <div style={{
                  position: "absolute", top: 0, left: "22%", right: "22%", height: "2px",
                  background: `linear-gradient(90deg, transparent, ${stat.color}, transparent)`,
                  borderRadius: "0 0 6px 6px",
                  boxShadow: `0 0 16px ${stat.color}`,
                }} />
                {/* Icon chip */}
                <div style={{
                  width: "30px", height: "30px", margin: "0 auto 8px", borderRadius: "10px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: `${stat.color}1F`, border: `1px solid ${stat.color}55`,
                  boxShadow: `0 0 12px ${stat.glow}`, position: "relative",
                }}>
                  <stat.Icon size={16} weight="bold" color={stat.color} />
                </div>
                {/* Number */}
                <div style={{
                  fontSize: "clamp(1.7rem, 7.5vw, 2.6rem)",
                  fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1,
                  fontVariantNumeric: "tabular-nums", position: "relative",
                  background: `linear-gradient(180deg, #FFFFFF 20%, ${stat.color2} 60%, ${stat.color} 100%)`,
                  WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
                  filter: `drop-shadow(0 2px 10px ${stat.glow})`,
                }}>{stat.hasData ? stat.display : "—"}</div>
                {/* Progress bar (win rate) */}
                {stat.bar && stat.hasData && (
                  <div style={{ height: "4px", borderRadius: "99px", background: "rgba(255,255,255,0.08)", margin: "10px 14px 0", overflow: "hidden", position: "relative" }}>
                    <div className="stat-bar-fill" style={{
                      height: "100%", width: `${Math.min(stat.raw, 100)}%`, borderRadius: "99px",
                      background: `linear-gradient(90deg, ${stat.color}, ${stat.color2})`,
                      boxShadow: `0 0 10px ${stat.glow}`,
                    }} />
                  </div>
                )}
                {/* Label */}
                <div style={{ fontSize: "9px", fontWeight: 800, color: "#E5E7EB", letterSpacing: "2px", textTransform: "uppercase", marginTop: stat.bar && stat.hasData ? "8px" : "10px", position: "relative" }}>
                  {stat.label}
                </div>
                <div style={{ fontSize: "8px", color: "#6B7280", marginTop: "2px", position: "relative" }}>
                  {stat.sub}
                </div>
              </div>
            ))}
          </div>

          {/* PRUEBA REAL — así se ve una señal ganada por dentro */}
          <div className="mb-6 fade-in-up-2 text-left" data-testid="hero-proof-card">
            <WonExampleCard />
          </div>

          {/* CTA único */}
          {!isAuthenticated && (
            <div className="flex flex-col gap-2 fade-in-up-3">
              <Link to="/register" data-testid="hero-cta-register"
                className="btn-gold w-full py-5 text-base text-center block font-black tracking-widest uppercase"
                style={{
                  boxShadow: "0 12px 44px rgba(212,175,55,0.45), 0 0 0 1px rgba(212,175,55,0.3)",
                  animation: "cta-pulse 2.4s ease-in-out infinite",
                }}>
                {t("landing.hero_cta_trial")} →
              </Link>
              <div className="text-[11px]" style={{ color: "#9CA3AF" }}>
                1 día gratis · sin tarjeta ·{" "}
                <Link to="/login" className="underline" style={{ color: "#D4AF37" }}>ya tengo cuenta</Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── RÉCORD VERIFICADO — la prueba, de entrada ── */}
      <RecordSection />

      {/* ── WINS TICKER ───────────────────────────────────────── */}
      <WinsTicker />

      <GoldDivider />

      {/* ── LIGAS OFICIALES ─────────────────────────────────────── */}
      <LeagueStrip />

      {/* ── MODO GANADA banner ────────────────────────────────── */}
      <ModoGanada lastWinMinutes={lastWinMinutes} />

      {/* ── CURVA DE PROFIT — solo el gráfico, visible tras el hero ── */}
      <section className="py-8 px-4"
        style={{ background: "#1A1A2E", borderTop: "2px solid rgba(212,175,55,0.3)" }}
        data-testid="performance-section">
        <div className="max-w-4xl mx-auto">
          {/* Chart container */}
          <div className="rounded-2xl overflow-hidden relative" style={{
            background: "#23233A",
            border: "1px solid rgba(16,185,129,0.4)",
            borderTop: "2px solid rgba(16,185,129,0.7)",
            padding: "20px 16px 8px",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, transparent, #10B981, #D4AF37, #10B981, transparent)" }} />
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs font-black tracking-widest uppercase" style={{ color: "#D4AF37" }}>
                  CURVA DE PROFIT
                </div>
                <div className="text-xs mt-0.5" style={{ color: "#9090B0" }}>
                  Unidades acumuladas pick a pick — datos reales
                </div>
              </div>
              <a href="/historial" className="btn-lift text-xs font-bold px-3 py-1.5 rounded-full"
                style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.45)" }}
                data-testid="ver-historial-btn">
                Ver historial →
              </a>
            </div>
            <PerformanceChart />
            {weeklyHeat.length > 0 && (
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: 4 }}>
                <div style={{
                  fontSize: 9, fontWeight: 800, color: "#8888AA",
                  textTransform: "uppercase", letterSpacing: "0.22em",
                  textAlign: "center", paddingTop: 10,
                }}>
                  Rendimiento últimos 7 días
                </div>
                <WeeklyHeatBar heat={weeklyHeat} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── SIMULADOR DE GANANCIAS: ¿cuánto habrías ganado TÚ? ── */}
      <ProfitSimulator
        totalPicks={publicStats?.total_picks ?? 38}
        netUnits={publicStats?.final_profit ?? 2.1}
      />

      <GoldDivider />

      {/* ── NOTICIAS DEPORTIVAS ───────────────────────────────── */}
      <SportsNewsFeed />

      {/* ── IQ DEL DÍA — siempre visible, antes de los tabs ──── */}
      <div className="relative" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.025) 50%, transparent 100%)" }}>
        {/* Radial spotlight */}
        <div style={{
          position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
          width: "100%", maxWidth: 500, height: "100%",
          background: "radial-gradient(ellipse at 50% 30%, rgba(212,175,55,0.07) 0%, transparent 65%)",
          pointerEvents: "none", zIndex: 0,
        }} />
      <div className="max-w-5xl mx-auto px-4 pt-5 pb-1 relative" style={{ zIndex: 1 }} data-testid="iq-del-dia-section">
        {loading ? (
          <div className="rounded-2xl h-56 mb-4 animate-pulse" style={{ background: "#252540" }} />
        ) : signals.length >= 2 ? (
          /* ── Layout 2 IQ: banner + tarjetas completamente difuminadas + 1 solo CTA ── */
          <div data-testid="two-iq-layout">
            <TwoIQBanner count={signals.length} onUnlock={() => nav("/register")} />
            <div className="grid grid-cols-2 gap-2 mb-3">
              {signals.map((_, i) => (
                <CompactSignalCard key={i} isAuthenticated={isAuthenticated} index={i} />
              ))}
            </div>
            {!isAuthenticated && (
              <button
                onClick={() => nav("/register")}
                data-testid="two-iq-unlock-full-btn"
                className="w-full py-3.5 rounded-xl text-sm font-black text-center"
                style={{ background: "#D4AF37", color: "#1A1A2E", letterSpacing: "0.04em" }}
              >
                {t("landing.two_iq_cta", { count: signals.length })}
              </button>
            )}
          </div>
        ) : (
          /* ── Layout 1 IQ: tarjeta única normal ── */
          <SignalCard signal={signal} isAuthenticated={isAuthenticated} onUnlock={() => nav("/register")} />
        )}
      </div>
      </div>

      {/* ── SPORT TABS ──────────────────────────────────────────── */}
      <div className="flex overflow-x-auto sticky top-14 z-40"
        style={{ background: "#1A1A2E", borderBottom: "1px solid rgba(255,255,255,0.1)", scrollbarWidth: "none" }}>
        {SPORT_TABS.map(tab => {
          const active = selectedSport === tab.key;
          return (
            <button key={tab.key} onClick={() => setSelectedSport(tab.key)}
              data-testid={`sport-tab-${tab.key}`}
              className="flex items-center gap-2 px-5 py-3.5 flex-shrink-0 text-sm font-bold relative transition-all"
              style={{
                color: active ? "#F2F2F2" : "#9090AA",
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}>
              <tab.Icon className="w-4 h-4" weight="fill" />
              <span>{t(tab.labelKey)}</span>
              {active && <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: "#D4AF37" }} />}
            </button>
          );
        })}
      </div>

      {/* ── PARTIDOS DEL DÍA ────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 pt-5" style={{ position: "relative" }}>

        {/* Tennis empty */}
        {selectedSport === "tennis" && (
          <div className="rounded-2xl p-8 text-center mb-4" style={{ background: "#252540", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="tennis-empty">
            <TennisBall className="w-10 h-10 mx-auto mb-3" weight="fill" style={{ color: "#52525B" }} />
            <div className="text-sm font-bold text-white mb-1">{t("landing.tennis_soon")}</div>
            <div className="text-xs" style={{ color: "#8080A0" }}>{t("landing.tennis_sub")}</div>
          </div>
        )}

        {selectedSport !== "tennis" && (
          <>
            {/* ── SKELETON mientras carga ── */}
            {loading && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-20 h-4 rounded animate-pulse" style={{ background: "#252540" }} />
                </div>
                <MatchCardSkeletons count={6} compact />
              </div>
            )}

            {/* Live matches — grid de 2 columnas, mismo tamaño que upcoming */}
            {!loading && liveMatches.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#10B981" }} />
                  <span className="text-base font-black text-white">{t("landing.live_now")}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-bold" style={{ background: "rgba(16,185,129,0.1)", color: "#10B981", border: "1px solid rgba(16,185,129,0.25)" }}>
                    {liveMatches.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 lg:gap-3">
                  {liveMatches.map((m, i) => <LiveMatchCard key={m.id || i} match={m} />)}
                </div>
              </div>
            )}

            {/* Vista TODOS — mezcla próximos + resultados del día por hora */}
            {!loading && selectedSport === "all" && allDayMatches.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-base font-black text-white">{t("landing.day_matches")}</span>
                  <span className="text-[11px] px-2.5 py-1 rounded-full font-bold" style={{ background: "#252540", color: "#B0B0C8", border: "1px solid rgba(255,255,255,0.08)" }}>
                    {t("landing.matches_count", { count: allDayMatches.length })}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 lg:gap-3 fade-in-up-4">
                  {allDayMatches.map((m, i) => <CompactMatchCard key={m.id || i} match={m} />)}
                </div>
              </div>
            )}

            {/* Vista por deporte — upcoming 2 cols, recent 2 cols */}
            {!loading && selectedSport !== "all" && (
              <>
                {upcomingMatches.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-base font-black text-white">{t("landing.upcoming")}</span>
                      <span className="text-[11px] px-2.5 py-1 rounded-full font-bold" style={{ background: "#252540", color: "#B0B0C8", border: "1px solid rgba(255,255,255,0.08)" }}>
                        {t("landing.matches_count", { count: upcomingMatches.length })}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 lg:gap-3">
                      {upcomingMatches.map((m, i) => <CompactMatchCard key={m.id || i} match={m} />)}
                    </div>
                  </div>
                )}
                {recentMatches.length > 0 && liveMatches.length === 0 && upcomingMatches.length === 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-base font-black text-white">{t("landing.recent_results")}</span>
                      <span className="text-[11px] px-2.5 py-1 rounded-full font-bold" style={{ background: "#252540", color: "#B0B0C8", border: "1px solid rgba(255,255,255,0.08)" }}>
                        {t("landing.matches_count", { count: recentMatches.length })}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {recentMatches.map((m, i) => <CompactMatchCard key={m.id || i} match={m} />)}
                    </div>
                  </div>
                )}
              </>
            )}

            {!loading && filteredMatches.length === 0 && (
              <div className="rounded-2xl p-8 text-center mb-4" style={{ background: "#252540", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="no-matches-state">
                <CalendarBlank className="w-8 h-8 mx-auto mb-2" weight="fill" style={{ color: "#52525B" }} />
                <div className="text-base font-bold text-white mb-1">{t("landing.no_matches")}</div>
                <div className="text-sm" style={{ color: "#B0B0C8" }}>{t("landing.no_matches_sub")}</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── HOT / COLD TEAMS ────────────────────────────────────── */}
      {hotColdTeams && <HotColdSection data={hotColdTeams} />}

      {/* ── HISTORIAL DE RENDIMIENTO — estadísticas al fondo ─────── */}
      <section className="py-12 px-4" style={{ background: "#1A1A2E" }} data-testid="historial-stats-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
              style={{ background: "rgba(212,175,55,0.18)", border: "1px solid rgba(212,175,55,0.5)" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: "#D4AF37" }} />
              <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: "#D4AF37" }}>
                TRANSPARENCIA TOTAL
              </span>
            </div>
            <h2 className="font-sport text-white mb-2" style={{ fontSize: "clamp(1.6rem, 5vw, 2.6rem)", letterSpacing: "-0.025em" }}>
              Historial de <span className="text-gradient-gold">Rendimiento</span>
            </h2>
            <p className="text-base max-w-md mx-auto" style={{ color: "#C0C0D8" }}>
              Registros reales. Sin trampa. Cada pick documentado y verificable.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Efectividad",      value: publicStats ? `${publicStats.win_rate > 0 ? publicStats.win_rate : "—"}%` : "—",  color: "#10B981", Icon: TrendUp },
              { label: "Picks Totales", value: publicStats ? (publicStats.total_picks > 0 ? publicStats.total_picks : "—") : "—", color: "#D4AF37", Icon: Target },
              { label: "Ganados",       value: publicStats ? (publicStats.won_picks > 0 ? publicStats.won_picks : "—") : "—",     color: "#10B981", icon: "✓" },
              { label: "Miembros",      value: publicStats ? ((publicStats.total_users || publicStats.active_users || 0) > 0 ? `${publicStats.total_users || publicStats.active_users}+` : "—") : "—", color: "#C0A0FF", icon: "★" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl p-5 text-center"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
                  transition: "transform 0.2s ease, border-color 0.2s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: "50%",
                  margin: "0 auto 12px",
                  background: `${s.color}1A`,
                  border: `1px solid ${s.color}55`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20,
                  fontWeight: 900,
                  color: s.color,
                }}>{typeof s.icon === "string" ? s.icon : <s.Icon className="w-6 h-6" weight="fill" />}</div>
                <div style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "clamp(1.6rem, 5vw, 2.2rem)",
                  fontWeight: 900,
                  color: s.color,
                  letterSpacing: "-0.04em",
                  lineHeight: 1,
                  marginBottom: 8,
                }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 10, fontWeight: 800, color: "#B0B0CC", textTransform: "uppercase", letterSpacing: "0.15em" }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ───────────────────────────────────────── */}
      <section id="como-funciona" className="py-16 px-4 relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, #070709 0%, #0A0A12 100%)" }} data-testid="how-it-works">
        {/* Grid pattern overlay */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          pointerEvents: "none",
        }} />
        <div className="max-w-4xl mx-auto relative" style={{ zIndex: 1 }}>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
              style={{ background: "rgba(212,175,55,0.07)", border: "1px solid rgba(212,175,55,0.2)" }}>
              <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: "#D4AF37" }}>
                METODOLOGÍA
              </span>
            </div>
            <h2 className="font-sport text-white mb-3" style={{ fontSize: "clamp(1.8rem, 5vw, 2.8rem)", letterSpacing: "-0.025em" }}>{t("landing.how_title")}</h2>
            <p className="text-sm max-w-md mx-auto" style={{ color: "#9898B8" }}>{t("landing.how_sub")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HOW_STEPS.map((step, i) => {
              const isFirst = i === 0;
              const stepColors = ["rgba(212,175,55,", "rgba(16,185,129,", "rgba(99,102,241,", "rgba(244,63,94,"];
              const c = stepColors[i];
              return (
                <div key={step.n} className="relative rounded-2xl p-5" data-testid={`how-step-${step.n}`}
                  style={{
                    background: isFirst
                      ? "linear-gradient(145deg, #13110A 0%, #0E0C07 100%)"
                      : "linear-gradient(145deg, #0E0E16 0%, #090912 100%)",
                    border: `1px solid ${c}${isFirst ? "0.40" : "0.18"})`,
                    borderTop: `2px solid ${c}${isFirst ? "0.60" : "0.30"})`,
                    boxShadow: isFirst ? `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${c}0.08)` : "0 4px 20px rgba(0,0,0,0.4)",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; }}
                >
                  {/* Top gradient bar */}
                  <div style={{ position: "absolute", top: 0, left: "20%", right: "20%", height: 1, background: `linear-gradient(90deg, transparent, ${c}0.50), transparent)` }} />
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{
                      background: `${c}0.12)`,
                      border: `1px solid ${c}${isFirst ? "0.35" : "0.20"})`,
                    }}>
                    <step.Icon className="w-6 h-6" weight="fill" style={{ color: `${c}0.85)` }} />
                  </div>
                  <div className="text-[10px] font-black tracking-widest mb-2"
                    style={{ color: isFirst ? "#D4AF37" : "#444" }}>PASO {step.n}</div>
                  <div className="text-sm font-black text-white mb-2">{t(step.titleKey)}</div>
                  <div className="text-xs leading-relaxed" style={{ color: "#9090AA" }}>{t(step.descKey)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <GoldDivider />

      {/* ── PRICING ─────────────────────────────────────────────── */}
      <section className="py-12 px-4 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #050507 0%, #080810 100%)" }} data-testid="pricing-section">
        {/* Subtle grid */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "linear-gradient(rgba(212,175,55,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.02) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          pointerEvents: "none",
        }} />
        <div className="max-w-4xl mx-auto relative" style={{ zIndex: 1 }}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
              style={{ background: "rgba(212,175,55,0.07)", border: "1px solid rgba(212,175,55,0.2)" }}>
              <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: "#D4AF37" }}>PLANES</span>
            </div>
            <h2 className="text-2xl font-black text-white">{t("landing.plans_title")}</h2>
            <p className="text-sm mt-2" style={{ color: "#B0B0C8" }}>{t("landing.plans_sub")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <div key={plan.id} className={`rounded-2xl overflow-hidden ${plan.highlight ? "pricing-card-premium" : ""}`}
                style={{
                  background: plan.highlight
                    ? "linear-gradient(155deg, #141208 0%, #181510 50%, #100E08 100%)"
                    : "linear-gradient(160deg, #15151d 0%, #101016 60%, #0d0d12 100%)",
                  border: `${plan.highlight ? "2px" : "1px"} solid ${plan.highlight ? "#D4AF37" : "rgba(255,255,255,0.09)"}`,
                  position: "relative",
                  boxShadow: plan.highlight
                    ? "0 0 44px rgba(212,175,55,0.16), 0 12px 36px rgba(0,0,0,0.5), inset 0 1px 0 rgba(212,175,55,0.15)"
                    : "0 8px 28px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
                }}
                data-testid={`plan-${plan.id}`}>
                {!plan.highlight && (
                  <div style={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)" }} />
                )}
                {plan.badgeKey && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 px-4 py-1 text-[10px] font-black tracking-widest whitespace-nowrap"
                    style={{ background: "linear-gradient(135deg,#E8C766,#B8962E)", color: "#000", borderRadius: "0 0 10px 10px", boxShadow: "0 4px 16px rgba(212,175,55,0.45)" }}>
                    {t(plan.badgeKey)}
                  </div>
                )}
                {plan.highlight && <div style={{ height: 2, background: "linear-gradient(90deg, transparent, #D4AF37, #F0C96A, #D4AF37, transparent)" }} />}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: plan.highlight ? "#D4AF37" : "#555" }}>
                        {t(`landing.plan_${plan.id}_name`)}
                      </div>
                      <div className="flex items-end gap-1 mt-1">
                        <span className={`text-4xl font-black ${plan.highlight ? "text-gradient-gold" : "text-white"}`}
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}>{plan.price}</span>
                        <span className="text-sm pb-1.5" style={{ color: "#9090AA" }}>{t(`landing.plan_${plan.id}_period`)}</span>
                      </div>
                    </div>
                    {plan.highlight && <Sparkle weight="fill" className="w-5 h-5 mt-1" style={{ color: "#D4AF37" }} />}
                  </div>
                  <div className="space-y-2 mb-5">
                    {plan.fKeys.map((fk, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs" style={{ color: "#ccc" }}>
                        <CheckCircle weight="fill" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: plan.highlight ? "#D4AF37" : "#10B981" }} />
                        {t(`landing.${fk}`)}
                      </div>
                    ))}
                  </div>
                  <Link to={plan.href}
                    className={`btn-lift block w-full py-3.5 rounded-xl font-black text-xs text-center tracking-widest uppercase transition-all duration-200 ${plan.highlight ? "btn-gold" : ""}`}
                    style={!plan.highlight ? {
                      background: "#16161E",
                      color: "#B0B0C8",
                      border: "1px solid rgba(255,255,255,0.08)",
                    } : {}}
                    data-testid={`plan-cta-${plan.id}`}>
                    {t(`landing.plan_${plan.id}_cta`)}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CASAS DE APUESTAS (afiliados) — después de precios, sin interrumpir el embudo ── */}
      <div className="max-w-5xl mx-auto px-4 pt-5 pb-1" data-testid="bookmakers-section">
        <BookmakersCarousel />
      </div>

      {/* ── FINAL CTA ───────────────────────────────────────────── */}
      {!isAuthenticated && (
        <section className="py-14 px-4 relative overflow-hidden"
          style={{ background: "linear-gradient(180deg, #06060A 0%, #0A0A10 100%)", borderTop: "1px solid rgba(212,175,55,0.1)" }}>
          {/* Radial spotlight */}
          <div style={{
            position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
            width: "100%", maxWidth: 700, height: "100%",
            background: "radial-gradient(ellipse at 50% 50%, rgba(212,175,55,0.08) 0%, transparent 65%)",
            pointerEvents: "none",
          }} />
          <div className="max-w-2xl mx-auto text-center relative" style={{ zIndex: 1 }}>
            <div style={{
              background: "linear-gradient(145deg, rgba(18,16,10,0.95) 0%, rgba(13,11,7,0.98) 100%)",
              border: "1px solid rgba(212,175,55,0.25)",
              borderTop: "2px solid rgba(212,175,55,0.5)",
              borderRadius: 24,
              padding: "40px 32px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(212,175,55,0.06)",
            }} data-testid="final-cta">
              {/* Gold hairline */}
              <div style={{ height: 1, background: "linear-gradient(90deg, transparent, #D4AF37, #F0C96A, #D4AF37, transparent)", marginBottom: 28, borderRadius: 2 }} />
              <Lightning weight="fill" className="w-8 h-8 mx-auto mb-4" style={{ color: "#D4AF37", filter: "drop-shadow(0 0 12px rgba(212,175,55,0.6))" }} />
              <h2 className="font-sport text-white mb-2" style={{ fontSize: "clamp(1.8rem, 5vw, 3rem)", letterSpacing: "-0.02em" }}>
                {t("landing.final_cta_title")}
              </h2>
              <p className="text-sm mb-8" style={{ color: "#71717A" }}>
                {t("landing.final_cta_sub")}
              </p>
              <Link to="/register" className="btn-gold block w-full py-4 text-sm text-center tracking-widest uppercase font-black"
                data-testid="final-cta-register">
                {t("landing.final_cta_btn")}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── FOOTER ──────────────────────────────────────────────── */}
      <footer className="py-8 px-4" style={{ background: "#1A1A2E", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <div className="max-w-xl mx-auto">
          <div className="flex items-center justify-between mb-5">
            <GcrLogo size={30} />
            <span className="text-xs" style={{ color: "#52525B" }}>{t("landing.footer_tagline")}</span>
          </div>
          <div className="divider-premium mb-5" />
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs mb-5" style={{ color: "#52525B" }}>
            <Link to="/jugadas" className="hover:text-white transition-colors">{t("nav.picks")}</Link>
            <Link to="/planes" className="hover:text-white transition-colors">{t("nav.plans")}</Link>
            <Link to="/login" className="hover:text-white transition-colors">{t("nav.login")}</Link>
            <Link to="/register" className="hover:text-white transition-colors">{t("nav.register")}</Link>
          </div>
          <p className="text-[10px] leading-relaxed" style={{ color: "#3F3F46" }}>
            {t("landing.footer_disclaimer")}
          </p>
          <div className="text-[10px] mt-3" style={{ color: "#3F3F46" }}>{t("landing.footer_copyright")}</div>

          {/* Social / Contact */}
          <div className="mt-5 pt-5 flex flex-col items-center gap-3" style={{ borderTop: "1px solid #1F1F23" }}>

            {/* Personal developer note */}
            <p style={{ fontSize: 11, color: "#3F3F46", textAlign: "center", lineHeight: 1.6 }}>
              ¿Te gusta la plataforma? Me encantaría escucharte —{" "}
              <a
                href="mailto:melver@visionariatech.com"
                data-testid="footer-contact-email"
                style={{ color: "#71717A", textDecoration: "underline", textDecorationColor: "rgba(212,175,55,0.3)" }}
              >
                melver@visionariatech.com
              </a>
            </p>
          </div>
        </div>
      </footer>

      <ChatFAB />
    </div>
  );
}
